/**
 * Booking-rail layout gate — classic (pre-r2 prod parity) vs wide (r2).
 *
 * Decides the geometry of the scheduling rails (create / inline-edit /
 * block panels): which screen edge they sit on, their width, and their
 * DOM position relative to the calendar.
 *
 *   - 'classic' → LEFT edge, fixed 380px, rendered BEFORE the calendar.
 *     Exactly what production clinics know today (Flutter parity ~392px,
 *     `calendar_screen.dart:2738`).
 *   - 'wide'    → RIGHT edge, `clamp(420px,33vw,560px)`, rendered AFTER
 *     the calendar ("two-thirds for the scheduling, one-third for the
 *     rail" — Samer 2026-08-12; drawn for the Scheduling-AI rail).
 *
 * Reads `VITE_BOOKING_RAIL_WIDE_ORGS` — a per-ORGANIZATION allow-list so
 * the new layout can pilot at a single clinic before general rollout
 * (Samer 2026-08-21: validate the layout standalone, one clinic first,
 * so the AI later launches on an already-accepted UI — one launch, not
 * two). Values (trimmed, case-insensitive):
 *
 *   - unset / ''        → 'classic' everywhere (prod default; rollback
 *                          state — remove an org id or the whole var and
 *                          redeploy to restore the known layout)
 *   - '*'               → 'wide' everywhere (dev)
 *   - 'id1,id2,…' (csv) → 'wide' only for those organization ids
 *
 * Vite inlines the value at build time (same trade-off as
 * `use-clinic-edit-rail-enabled.ts`): toggling requires a redeploy of the
 * web bundle (~minutes via workflow_dispatch), not a code change. The
 * per-org RUNTIME kill-switch (flip with no deploy) is the planned
 * upgrade once an org-scoped feature endpoint exists server-side — this
 * hook is the single seam to re-point when it does.
 *
 * Scope note: this gates the CALENDAR-FRAME rails only. The full-page
 * appointment editor (`view-appointment-panel-page.tsx`, the 3-dots
 * path) keeps its form-on-the-LEFT layout in both modes (Flutter
 * `view_appointment_screen` parity); only its width follows the mode.
 * Whether that page should flip too is an explicit pilot-feedback
 * question, not decided here.
 */

export type BookingRailLayout = 'classic' | 'wide';

/**
 * Pure resolver — exported for unit testing; production callers use the
 * hook. `allowList` is the raw env string; `organizationId` the current
 * org (undefined while auth is still resolving → classic, fail-closed).
 */
export function resolveBookingRailLayout(
  allowList: unknown,
  organizationId: string | null | undefined,
): BookingRailLayout {
  if (typeof allowList !== 'string') return 'classic';
  const trimmed = allowList.trim();
  if (trimmed === '') return 'classic';
  if (trimmed === '*') return 'wide';
  if (!organizationId) return 'classic';
  const target = organizationId.trim().toLowerCase();
  const listed = trimmed
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean)
    .includes(target);
  return listed ? 'wide' : 'classic';
}

/**
 * Returns the booking-rail layout for the current build + organization.
 */
export function useBookingRailLayout(
  organizationId: string | null | undefined,
): BookingRailLayout {
  return resolveBookingRailLayout(
    import.meta.env.VITE_BOOKING_RAIL_WIDE_ORGS,
    organizationId,
  );
}
